import { Formik, Form } from 'formik'
import { useSelector } from 'react-redux'
import { toast } from 'sonner'
import {
  initialValues,
  getValidationSchema,
} from '@/pages/AdvisorSettings/validation'
import { fetchAdvisorDataThunk } from '@/redux/asyncThunks/configuration'
import { RootState } from '@/redux/store'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  AdvisorDataWithFiles,
  ChatbotType,
  DocumentRAGSettings,
} from '@/redux/types/configuration'
import { saveAdvisorData } from '@/api/configuration'
import { fetchAdvisorsThunk } from '@/redux/asyncThunks/advisor'
import { setUploadedFiles } from '@/redux/slices/configurationSlice'
import { useNavigate, useParams } from 'react-router-dom'
import { GameRoomStatus, LLMChoice } from '@/redux/types/room'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  closeTestChat,
  connectTestChatWebSocket,
  disconnectTestChatWebSocket,
  getPreviousChats,
} from '@/redux/asyncThunks/testChatWebsocket'
import {
  clearMessages,
  setIsTestChatOpen,
  addOpenChatBox,
  addTestChatBox,
  removeTestChatBox,
  removeOpenChatBox,
  clearAllOpenChatBoxes,
} from '@/redux/slices/testChatSlice'
import FileUpload from './FileUpload'
import LLMConfigSection from './LLMConfigSection'
import RAGConfigSection from './RAGConfigSection'
import PreviousTestChatsModal from './PreviousTestChatsModal'
import { SidebarProvider } from '@/components/ui/sidebar'
import { TooltipProvider } from '@/components/ui/tooltip'
import LlmPreference, {
  LlmPreferenceRef,
} from '@/components/Room/LlmPreference'
import { getAccessToken } from '@/utils/authUtils'
import ConfigSideBar, { ActiveSection } from './ConfigSideBar'
import BehaviorSection from './BehaviorSection'
import ChatBoxesRenderer from './ChatBoxesRenderer'
import FormFooter from './FormFooter'
import MCPConfig from './MCPConfig'

interface ConfigurationFormProps {
  onCancel: () => void
  chatbotid: string
}

export default function ConfigurationForm({
  onCancel,
  chatbotid,
}: ConfigurationFormProps) {
  const dispatch = useAppDispatch()
  const { files: uploadedFiles = [] } = useSelector(
    (state: RootState) => state.configuration
  )

  const navigate = useNavigate()
  const jwtToken = getAccessToken()

  const { error } = useSelector((state: RootState) => state.configuration)
  const [advisorData, setAdvisorData] = useState(initialValues)
  const [useRoomDefaultDocuments, setUseRoomDefaultDocuments] = useState<boolean>(false)
  const [useRoomDefaultBehavioral, setUseRoomDefaultBehavioral] = useState<boolean>(false)
  const [chatbotType, setChatbotType] = useState<ChatbotType | null>(null)
  const { currentRoom } = useAppSelector((state: RootState) => state.room)
  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const { existingTestChats, openChatBoxes } = useAppSelector(
    (state: RootState) => state.testChatMessages
  )
  const isTestChatOpen = useMemo(
    () => openChatBoxes.some((box) => box.isTestChat),
    [openChatBoxes]
  )
  const [previousChatModalOpen, setPreviousChatModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const latestRequestedChatbotIdRef = useRef<string | null>(null)

  const params = useParams()
  
  const roomId = params.roomId as string
  const llmPreferenceRef = useRef<LlmPreferenceRef>(null)

  useEffect(() => {
    if (chatbotid && roomId && jwtToken) {
      dispatch(
        connectTestChatWebSocket({ jwtToken, chatbotId: chatbotid, roomId })
      )
    }
    return () => {
      dispatch(disconnectTestChatWebSocket())
    }
  }, [jwtToken, chatbotid, roomId])
  useEffect(() => {
    latestRequestedChatbotIdRef.current = chatbotid
    dispatch(fetchAdvisorDataThunk(chatbotid))
      .unwrap()
      .then((data) => {
        if (latestRequestedChatbotIdRef.current !== chatbotid) {
          return
        }
        setAdvisorData({
          greetings: data.greetings || '',
          prompt: data.prompt || '',
          scenario: data.scenario || '',
          restrictions: data.restrictions || '',
          temperature: data.temperature !== undefined ? data.temperature : 0.7,
          max_tokens: data.max_tokens || 1024,
          history_limit: data.history_limit || 25,
          chatbot_llm: data.chatbot_llm || LLMChoice.CLAUDE,
          rag_enabled: data.rag_enabled !== undefined ? data.rag_enabled : true,
          rag_top_k: data.rag_top_k || 5,
          rag_chunk_size: data.rag_chunk_size || 1000,
          rag_chunk_overlap: data.rag_chunk_overlap || 200,
          rag_similarity_threshold:
            data.rag_similarity_threshold !== undefined
              ? data.rag_similarity_threshold
              : 0.2,
          rag_max_context_length: data.rag_max_context_length || 3000,
          currency_cost: data.currency_cost !== undefined && data.currency_cost !== null ? data.currency_cost : 0,
        })

        dispatch(setUploadedFiles(data.files || []))
        setChatbotType(data.chatbottype || null)

        if (
          data.chatbottype === ChatbotType.RANDR ||
          data.chatbottype === ChatbotType.ADJUDICATION
        ) {
          setUseRoomDefaultDocuments(false)
          setUseRoomDefaultBehavioral(false)
        } else {
          setUseRoomDefaultDocuments(!!data.use_room_default_documents)
          setUseRoomDefaultBehavioral(!!data.use_room_default_behavioral_config)
        }
      })
      .catch((error) => {
        if (latestRequestedChatbotIdRef.current !== chatbotid) {
          return
        }
        console.error('Failed to fetch advisor data', error)
      })
  }, [chatbotid, dispatch])

  const handleSave = useCallback(
    async (values: typeof initialValues) => {
      if (isGameCompleted) return

      setIsSaving(true)

      const fileIds = uploadedFiles.map((file) => file.id)

      const currentLLM =
        llmPreferenceRef.current?.getSelectedLLM() || values.chatbot_llm

      const documentSettings: Record<string, DocumentRAGSettings> = {}
      uploadedFiles.forEach((file) => {
        if (file.rag_settings) {
          documentSettings[file.id.toString()] = file.rag_settings
        }
      })

      const isRoomTogglesSupported =
        chatbotType !== ChatbotType.RANDR &&
        chatbotType !== ChatbotType.ADJUDICATION
      const roomDocs = isRoomTogglesSupported ? useRoomDefaultDocuments : false
      const roomBehavior = isRoomTogglesSupported ? useRoomDefaultBehavioral : false

      const advisorDataFlat: AdvisorDataWithFiles = {
        greetings: values.greetings,
        prompt: values.prompt,
        scenario: values.scenario,
        restrictions: values.restrictions,
        file_ids: fileIds,
        temperature: values.temperature,
        max_tokens: values.max_tokens,
        history_limit: values.history_limit,
        chatbot_llm: currentLLM,
        rag_enabled: values.rag_enabled,
        rag_top_k: values.rag_top_k,
        rag_chunk_size: values.rag_chunk_size,
        rag_chunk_overlap: values.rag_chunk_overlap,
        rag_similarity_threshold: values.rag_similarity_threshold,
        rag_max_context_length: values.rag_max_context_length,
        currency_cost: values.currency_cost,
        document_settings_data:
          Object.keys(documentSettings).length > 0 ? documentSettings : undefined,
        use_room_default_documents: roomDocs,
        use_room_default_behavioral_config: roomBehavior,
      }

      try {
        await saveAdvisorData(chatbotid, {
          advisorData: advisorDataFlat,
        })
        toast.success('Advisor data saved successfully!')
        const roomId = params.roomId as string
        if (roomId) {
          dispatch(fetchAdvisorsThunk({ roomId }))
        }
      } catch (error) {
        if (error instanceof Error) {
          toast.error(`Error saving advisor data: ${error.message}`)
        } else {
          toast.error('An unknown error occurred')
        }
      } finally {
        setIsSaving(false)
      }
    },
    [
      isGameCompleted,
      uploadedFiles,
      chatbotid,
      dispatch,
      params.roomId,
      chatbotType,
      useRoomDefaultDocuments,
      useRoomDefaultBehavioral,
    ]
  )

  const handleSubmit = useCallback(
    async (
      values: typeof initialValues,
      {
        setSubmitting,
      }: {
        setSubmitting: (isSubmitting: boolean) => void
      }
    ) => {
      if (isGameCompleted) return

      const fileIds = uploadedFiles.map((file) => file.id)

      const currentLLM =
        llmPreferenceRef.current?.getSelectedLLM() || values.chatbot_llm

      // Build document_settings map from uploadedFiles
      const documentSettings: Record<string, DocumentRAGSettings> = {}
      uploadedFiles.forEach((file) => {
        if (file.rag_settings) {
          documentSettings[file.id.toString()] = file.rag_settings
        }
      })

      const isRoomTogglesSupported =
        chatbotType !== ChatbotType.RANDR &&
        chatbotType !== ChatbotType.ADJUDICATION
      const roomDocs = isRoomTogglesSupported ? useRoomDefaultDocuments : false
      const roomBehavior = isRoomTogglesSupported ? useRoomDefaultBehavioral : false

      const advisorDataFlat: AdvisorDataWithFiles = {
        greetings: values.greetings,
        prompt: values.prompt,
        scenario: values.scenario,
        restrictions: values.restrictions,
        file_ids: fileIds,
        temperature: values.temperature,
        max_tokens: values.max_tokens,
        history_limit: values.history_limit,
        chatbot_llm: currentLLM,
        rag_enabled: values.rag_enabled,
        rag_top_k: values.rag_top_k,
        rag_chunk_size: values.rag_chunk_size,
        rag_chunk_overlap: values.rag_chunk_overlap,
        rag_similarity_threshold: values.rag_similarity_threshold,
        rag_max_context_length: values.rag_max_context_length,
        currency_cost: values.currency_cost,
        document_settings_data:
          Object.keys(documentSettings).length > 0 ? documentSettings : undefined,
        use_room_default_documents: roomDocs,
        use_room_default_behavioral_config: roomBehavior,
      }

      try {
        await saveAdvisorData(chatbotid, {
          advisorData: advisorDataFlat,
        })
        toast.success('Advisor data saved successfully!')
        navigate(0)
        const roomId = params.roomId as string
        if (roomId) {
          dispatch(fetchAdvisorsThunk({ roomId }))
        }
      } catch (error) {
        if (error instanceof Error) {
          toast.error(`Error saving advisor data: ${error.message}`)
        } else {
          toast.error('An unknown error occurred')
        }
      } finally {
        setSubmitting(false)
      }
    },
    [
      isGameCompleted,
      uploadedFiles,
      chatbotid,
      dispatch,
      params.roomId,
      navigate,
      chatbotType,
      useRoomDefaultDocuments,
      useRoomDefaultBehavioral,
    ]
  )

  const handleChatClose = useCallback(() => {
    dispatch(closeTestChat())
    dispatch(clearMessages())
    dispatch(setIsTestChatOpen(false))
    dispatch(removeTestChatBox())
  }, [dispatch])

  const handlePreviousChatClose = useCallback(() => {
    setPreviousChatModalOpen(false)
  }, [])

  useEffect(() => {
    return () => {
      dispatch(setIsTestChatOpen(false))
      dispatch(clearAllOpenChatBoxes())
    }
  }, [dispatch])
  const handleCancelClick = useCallback(() => {
    onCancel()
  }, [onCancel])

  const handleTestChatClick = useCallback(() => {
    if (openChatBoxes.length < 3 || isTestChatOpen) {
      dispatch(setIsTestChatOpen(true))
      dispatch(addTestChatBox())
    } else {
      toast.error(
        'Maximum of 3 chat boxes allowed. Please close one before opening test chat.'
      )
    }
  }, [openChatBoxes.length, isTestChatOpen, dispatch])

  const handlePreviousChatsClick = useCallback(() => {
    dispatch(getPreviousChats())
    setPreviousChatModalOpen(true)
  }, [dispatch])

  const handleViewChat = useCallback(
    (chatId: number) => {
      if (
        openChatBoxes.length >= 3 &&
        !openChatBoxes.find((box) => box.id === chatId)
      ) {
        toast.error(
          'Maximum of 3 chat boxes allowed. Please close one before opening another.'
        )
        return
      }

      const selectedChat = existingTestChats.find((chat) => chat.id === chatId)

      if (selectedChat) {
        dispatch(addOpenChatBox({ chatData: selectedChat, isReadOnly: true }))
      }

      setPreviousChatModalOpen(false)
    },
    [openChatBoxes, existingTestChats, dispatch]
  )

  const handleCloseChatBox = useCallback(
    (chatId: number) => {
      dispatch(removeOpenChatBox(chatId))
    },
    [dispatch]
  )

  const roomTogglesApplicable =
    chatbotType !== ChatbotType.RANDR &&
    chatbotType !== ChatbotType.ADJUDICATION

  const validationSchema = useMemo(
    () =>
      getValidationSchema(
        chatbotType,
        roomTogglesApplicable && useRoomDefaultBehavioral
      ),
    [chatbotType, roomTogglesApplicable, useRoomDefaultBehavioral]
  )

  const handleViewMultipleChats = useCallback(
    (chatIds: number[]) => {
      chatIds.forEach((chatId) => {
        if (
          openChatBoxes.length >= 3 &&
          !openChatBoxes.find((box) => box.id === chatId)
        ) {
          toast.error(
            `Maximum of 3 chat boxes allowed. Only opening ${3 - openChatBoxes.length} chat(s).`
          )
          return
        }

        const selectedChat = existingTestChats.find(
          (chat) => chat.id === chatId
        )
        if (selectedChat) {
          dispatch(addOpenChatBox({ chatData: selectedChat, isReadOnly: true }))
        }
      })

      setPreviousChatModalOpen(false)
    },
    [openChatBoxes, existingTestChats, dispatch]
  )

  return (
    <SidebarProvider>
      <TooltipProvider>
        <div className='flex relative w-full min-h-screen'>
          <Formik
            initialValues={advisorData}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, values, setFieldValue }) => {
              const llmConfig = useMemo(
                () => ({
                  temperature: values.temperature,
                  maxTokens: values.max_tokens,
                  historyLimit: values.history_limit,
                }),
                [values.temperature, values.max_tokens, values.history_limit]
              )

              const ragConfig = useMemo(
                () => ({
                  ragEnabled: values.rag_enabled,
                  ragTopK: values.rag_top_k,
                  ragChunkSize: values.rag_chunk_size,
                  ragChunkOverlap: values.rag_chunk_overlap,
                  ragSimilarityThreshold: values.rag_similarity_threshold,
                  ragMaxContextLength: values.rag_max_context_length,
                }),
                [
                  values.rag_enabled,
                  values.rag_top_k,
                  values.rag_chunk_size,
                  values.rag_chunk_overlap,
                  values.rag_similarity_threshold,
                  values.rag_max_context_length,
                ]
              )

              const handleConfigChange = useCallback(
                (config: {
                  temperature: number
                  maxTokens: number
                  historyLimit: number
                }) => {
                  setFieldValue('temperature', config.temperature)
                  setFieldValue('max_tokens', config.maxTokens)
                  setFieldValue('history_limit', config.historyLimit)
                },
                [setFieldValue]
              )

              const handleRAGConfigChange = useCallback(
                (config: {
                  ragEnabled: boolean
                  ragTopK: number
                  ragChunkSize: number
                  ragChunkOverlap: number
                  ragSimilarityThreshold: number
                  ragMaxContextLength: number
                }) => {
                  setFieldValue('rag_enabled', config.ragEnabled)
                  setFieldValue('rag_top_k', config.ragTopK)
                  setFieldValue('rag_chunk_size', config.ragChunkSize)
                  setFieldValue('rag_chunk_overlap', config.ragChunkOverlap)
                  setFieldValue(
                    'rag_similarity_threshold',
                    config.ragSimilarityThreshold
                  )
                  setFieldValue(
                    'rag_max_context_length',
                    config.ragMaxContextLength
                  )
                },
                [setFieldValue]
              )

              const handleSaveClick = useCallback(() => {
                handleSave(values)
              }, [handleSave, values])

              return (
                <Form className='flex w-full'>
                  <ConfigSideBar />

                  <div className='flex flex-col flex-1'>
                    <div className='overflow-y-auto flex-1 p-8'>
                      {roomTogglesApplicable && (
                          <div className='mb-6 space-y-4 rounded-lg border border-border p-4'>
                            <p className='text-sm font-medium text-foreground'>
                              Shared Resources
                            </p>
                            <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                              <div className='space-y-1 flex-1'>
                                <Label
                                  htmlFor='use-room-docs'
                                  className='text-sm font-medium'
                                >
                                  Use World Default Documents
                                </Label>
                                <p className='text-xs text-muted-foreground'>
                                  When on, retrieval includes this advisor&apos;s
                                  documents and the world&apos;s shared resources. RAG
                                  limits below still apply to retrieval.
                                </p>
                              </div>
                              <Switch
                                id='use-room-docs'
                                checked={useRoomDefaultDocuments}
                                onCheckedChange={setUseRoomDefaultDocuments}
                                disabled={isGameCompleted}
                              />
                            </div>
                            <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                              <div className='space-y-1 flex-1'>
                                <Label
                                  htmlFor='use-room-behavior'
                                  className='text-sm font-medium'
                                >
                                  Use World Default Behavioral Configuration
                                </Label>
                                <p className='text-xs text-muted-foreground'>
                                  When on, greetings, prompt, scenario,
                                  restrictions, and currency cost come from
                                  World Setup.
                                </p>
                              </div>
                              <Switch
                                id='use-room-behavior'
                                checked={useRoomDefaultBehavioral}
                                onCheckedChange={setUseRoomDefaultBehavioral}
                                disabled={isGameCompleted}
                              />
                            </div>
                            {(useRoomDefaultDocuments ||
                              useRoomDefaultBehavioral) && (
                              <Alert>
                                <AlertDescription className='text-sm'>
                                  Configure world-level text, shared resources,
                                  and currency cost in World Setup. Model, RAG
                                  parameters, preferred LLM,
                                  and MCP servers stay on this advisor.
                                </AlertDescription>
                              </Alert>
                            )}
                          </div>
                        )}

                      <div id={ActiveSection.BEHAVIOR} className='mt-6'>
                        <BehaviorSection
                          chatbotType={chatbotType}
                          isGameCompleted={isGameCompleted}
                          openChatBoxesCount={openChatBoxes.length}
                          isTestChatOpen={isTestChatOpen}
                          onTestChatClick={handleTestChatClick}
                          onPreviousChatsClick={handlePreviousChatsClick}
                          disabled={false}
                          hideBehaviorTextFields={
                            roomTogglesApplicable && useRoomDefaultBehavioral
                          }
                        />
                      </div>
                      <div id={ActiveSection.CONFIG} className='mt-6'>
                        <LLMConfigSection
                          config={llmConfig}
                          onConfigChange={handleConfigChange}
                          disabled={
                            isGameCompleted || openChatBoxes.length !== 0
                          }
                        />
                      </div>

                      <div id={ActiveSection.RAG_CONFIG} className='mt-6'>
                        <RAGConfigSection
                          config={ragConfig}
                          onConfigChange={handleRAGConfigChange}
                          disabled={
                            isGameCompleted || openChatBoxes.length !== 0
                          }
                        />
                      </div>

                      <div id={ActiveSection.UPLOAD} className='mt-6'>
                        <FileUpload
                          chatbotid={chatbotid}
                          disabled={openChatBoxes.length !== 0}
                          advisorRAGDefaults={{
                            ragEnabled: values.rag_enabled,
                            ragTopK: values.rag_top_k,
                            ragChunkSize: values.rag_chunk_size,
                            ragChunkOverlap: values.rag_chunk_overlap,
                            ragSimilarityThreshold: values.rag_similarity_threshold,
                            ragMaxContextLength: values.rag_max_context_length,
                          }}
                        />
                      </div>

                      <div id={ActiveSection.LLM_PREFERENCE} className='mt-6'>
                        <LlmPreference
                          ref={llmPreferenceRef}
                          chatbotId={chatbotid}
                          chatbotLLM={advisorData.chatbot_llm}
                          disabled={openChatBoxes.length !== 0}
                        />
                      </div>

                      <div id={ActiveSection.MCP_SERVER} className='mt-6'>
                        <MCPConfig
                          disabled={openChatBoxes.length !== 0}
                          chatbotId={chatbotid}
                          roomId={roomId}
                        />
                      </div>
                    </div>
                    <FormFooter
                      isGameCompleted={isGameCompleted}
                      isSubmitting={isSubmitting}
                      isSaving={isSaving}
                      openChatBoxesCount={openChatBoxes.length}
                      onCancel={handleCancelClick}
                      onSave={handleSaveClick}
                    />
                  </div>

                  {error && (
                    <div className='p-6 pt-0'>
                      <div className='text-red-500'>{error}</div>
                    </div>
                  )}
                </Form>
              )
            }}
          </Formik>

          <ChatBoxesRenderer
            openChatBoxes={openChatBoxes}
            onTestChatClose={handleChatClose}
            onCloseChatBox={handleCloseChatBox}
          />

          <PreviousTestChatsModal
            isOpen={previousChatModalOpen}
            onClose={handlePreviousChatClose}
            onViewChat={handleViewChat}
            onViewMultipleChats={handleViewMultipleChats}
          />
        </div>
      </TooltipProvider>
    </SidebarProvider>
  )
}
